import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages the add to cart modal with collection tabs.
 *
 * @extends {DialogComponent}
 */
class CartModalComponent extends DialogComponent {
  /** @type {Array<{products: string, title: string}>} */
  #tabs = [];
  #activeTab = 0;
  #freeShippingThreshold = 0;
  #tabCache = new Map(); // Cache for tab products
  #productCache = new Map(); // Cache for individual product data
  #pendingFetches = new Map(); // Track pending product fetches to avoid duplicates
  #isModalOpen = false;

  connectedCallback() {
    super.connectedCallback();
    // Use capture phase to handle event before cart drawer
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { capture: true });

    // Get manual product selections from data attributes (6 tabs)
    this.#tabs = [
      {
        products: this.dataset['tab-1Products'] || '',
        title: this.dataset['tab-1Title'] || '',
      },
      {
        products: this.dataset['tab-2Products'] || '',
        title: this.dataset['tab-2Title'] || '',
      },
      {
        products: this.dataset['tab-3Products'] || '',
        title: this.dataset['tab-3Title'] || '',
      },
      {
        products: this.dataset['tab-4Products'] || '',
        title: this.dataset['tab-4Title'] || '',
      },
      {
        products: this.dataset['tab-5Products'] || '',
        title: this.dataset['tab-5Title'] || '',
      },
      {
        products: this.dataset['tab-6Products'] || '',
        title: this.dataset['tab-6Title'] || '',
      },
    ].filter((tab) => tab.products && tab.products.trim() !== '');

    this.#freeShippingThreshold = parseInt(this.dataset['freeShippingThreshold'] || '10000');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd, { capture: true });
    // Clear caches on disconnect
    this.#tabCache.clear();
    this.#productCache.clear();
    this.#pendingFetches.clear();
  }

  /**
   * Handles the cart add event and shows the modal with product details
   * @param {CartAddEvent} event
   */
  #handleCartAdd = async (event) => {
    // Don't show modal if there was an error adding to cart
    if (event.detail?.data?.didError) {
      return;
    }

    // Don't show modal if this came from quick add modal (it has its own flow)
    if (event.detail?.data?.source === 'quick-add') {
      return;
    }

    // Don't show modal if this came from the cart modal itself (adding from recommendations)
    if (event.detail?.data?.source === 'cart-modal') {
      return;
    }

    // Don't show modal if this came from cart items component (quantity changes in cart)
    if (event.detail?.data?.source === 'cart-items-component') {
      return;
    }

    // Don't show modal if item count is 0 or negative (removing items)
    const itemCount = event.detail?.data?.itemCount;
    if (itemCount !== undefined && itemCount <= 0) {
      return;
    }

    // Stop the event from reaching the cart drawer
    event.stopPropagation();

    // Get the added product information
    const variantId = event.detail?.sourceId; // sourceId is the variant ID

    if (!variantId) {
            // Fetch cart with sections for cart drawer update
      const cart = await this.#fetchCartWithSections();

      // Update cart subtotal and shipping progress
      this.#updateCartInfo(cart.total_price, cart.item_count);
      this.openCartDrawer();
      return;
    }

    // Fetch the cart to get the added item details
    try {
      // Close quick add modal if it's open
      this.#closeQuickAddModal();

      // Show the modal IMMEDIATELY (before async operations)
      this.showDialog();
      this.#isModalOpen = true;

      // Fetch cart with sections for cart drawer update
      const cart = await this.#fetchCartWithSections();

      // Update cart subtotal and shipping progress
      this.#updateCartInfo(cart.total_price, cart.item_count);

      // Setup tabs and load first tab asynchronously
      if (this.#tabs.length > 0) {
        this.#setupTabs();
        // Load first tab products asynchronously (non-blocking)
        await this.#loadTabProducts(0);
        // Prefetch next tab in background for faster switching
        this.#prefetchAdjacentTabs(0);
      } else {
        // Hide tabs section if no products configured
        this.#hideTabs();
      }
    } catch (error) {
      console.error('Cart modal error:', error);
    }
  };

  /**
   * Override closeDialog to update state
   */
  closeDialog() {
    this.#isModalOpen = false;
    super.closeDialog();
  }

  /**
   * Closes the quick add modal if it's open
   */
  #closeQuickAddModal() {
    const quickAddDialog = document.getElementById('quick-add-dialog');
    if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
      // @ts-ignore - quick-add-dialog has closeDialog method
      quickAddDialog.closeDialog();
    }
  }

  /**
   * Opens the cart drawer and closes the cart modal
   */
  openCartDrawer() {
    // Close this modal first
    this.closeDialog();

    // Open the cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      // Use setTimeout to ensure modal closes before drawer opens
      setTimeout(() => {
        // @ts-ignore - cart-drawer-component has open method
        cartDrawer.open();
      }, 100);
    }
  }

  /**
   * Fetches the current cart (with caching consideration)
   * @returns {Promise<any>}
   */
  async #fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return response.json();
  }

  /**
   * Fetches the current cart with sections for cart drawer update
   * @returns {Promise<any>}
   */
  async #fetchCartWithSections() {
    // Get all cart-items-component sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionsToFetch = [];

    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionsToFetch.push(component.dataset.sectionId);
      }
    });

    if (sectionsToFetch.length === 0) {
      // If no sections, just fetch regular cart
      return this.#fetchCart();
    }

    // Fetch cart with sections
    const params = new URLSearchParams({
      sections: sectionsToFetch.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`/cart/update.js?${params}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart with sections');
    }

    const data = await response.json();

    // Dispatch CartUpdateEvent with sections so cart drawer can update
    if (data.sections) {
      document.dispatchEvent(
        new CartUpdateEvent(
          data,
          '',
          {
            itemCount: data.item_count,
            source: 'cart-modal',
            sections: data.sections,
          }
        )
      );
    }

    return data;
  }

  /**
   * Updates cart subtotal and shipping progress together
   * @param {number} totalPrice - Total price in cents
   * @param {number} itemCount - Number of items in cart
   */
  #updateCartInfo(totalPrice, itemCount) {
    this.#updateSubtotal(totalPrice);
    this.#updateShippingProgress(totalPrice);
    this.#updateViewCartCount(itemCount);
  }

  /**
   * Updates the cart subtotal display
   * @param {number} totalPrice - Total price in cents
   */
  #updateSubtotal(totalPrice) {
    const subtotalEl = this.querySelector('[ref="subtotal"]');
    if (!subtotalEl) return;

    subtotalEl.innerHTML = `
      <span class="cart-modal__subtotal-label">Cart subtotal :</span>
      <span class="cart-modal__subtotal-amount">${this.#formatMoney(totalPrice)}</span>
    `;
  }

  /**
   * Updates the shipping progress indicator
   * @param {number} totalPrice - Total price in cents
   */
  #updateShippingProgress(totalPrice) {
    const progressEl = this.querySelector('[ref="shippingProgress"]');
    if (!progressEl) return;

    const remaining = this.#freeShippingThreshold - totalPrice;
    const percentage = Math.min((totalPrice / this.#freeShippingThreshold) * 100, 100);

    if (remaining <= 0) {
      // Free shipping unlocked
      progressEl.innerHTML = `
        <div class="cart-modal__shipping-message">
          <svg class="cart-modal__shipping-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <div class="cart-modal__shipping-text">
            <span class="cart-modal__shipping-emoji">🎉</span>
            You've unlocked <strong>free standard delivery!</strong>
          </div>
        </div>
        <div class="cart-modal__progress-bar">
          <div class="cart-modal__progress-fill" style="width: 100%"></div>
        </div>
      `;
    } else {
      // Show remaining amount
      progressEl.innerHTML = `
        <div class="cart-modal__shipping-message">
          <svg class="cart-modal__shipping-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
          <div class="cart-modal__shipping-text">
            <span class="cart-modal__shipping-emoji">🎉</span>
            You're <span class="cart-modal__shipping-amount">${this.#formatMoney(remaining)}</span> away from <strong>free standard delivery!</strong>
          </div>
        </div>
        <div class="cart-modal__progress-bar">
          <div class="cart-modal__progress-fill" style="width: ${percentage}%"></div>
        </div>
      `;
    }
  }

  /**
   * Updates the view cart button count
   * @param {number} itemCount - Number of items in cart
   */
  #updateViewCartCount(itemCount) {
    const countEl = this.querySelector('[ref="viewCartCount"]');
    if (!countEl) return;

    if (itemCount > 0) {
      countEl.textContent = ` (${itemCount})`;
    } else {
      countEl.textContent = '';
    }
  }

  /**
   * Hides the tabs section
   */
  #hideTabs() {
    const tabsSection = this.querySelector('[ref="collections"]');
    if (tabsSection instanceof HTMLElement) {
      tabsSection.style.display = 'none';
    }
  }

  /**
   * Sets up the tabs (only once)
   */
  #setupTabs() {
    const tabsContainer = this.querySelector('[ref="tabs"]');
    if (!tabsContainer || tabsContainer.children.length > 0) return; // Already setup

    const fragment = document.createDocumentFragment();

    this.#tabs.forEach((tab, index) => {
      const button = document.createElement('button');
      button.className = `cart-modal__tab ${index === 0 ? 'active' : ''}`;
      button.dataset.tabIndex = index.toString();
      button.textContent = tab.title;

      button.addEventListener('click', async () => {
        this.#activeTab = index;

        // Update active state
        tabsContainer.querySelectorAll('.cart-modal__tab').forEach((t) => t.classList.remove('active'));
        button.classList.add('active');

        // Load tab products
        await this.#loadTabProducts(index);

        // Prefetch adjacent tabs in background
        this.#prefetchAdjacentTabs(index);
      });

      fragment.appendChild(button);
    });

    tabsContainer.appendChild(fragment);
  }

  /**
   * Loads products from manual selection (with caching)
   * @param {number} tabIndex - The index of the tab to load
   */
  async #loadTabProducts(tabIndex) {
    const tab = this.#tabs[tabIndex];
    if (!tab) return;

    const productsGrid = this.querySelector('[ref="productsGrid"]');
    if (!productsGrid) return;

    // Check cache first
    const cacheKey = `tab-${tabIndex}`;
    if (this.#tabCache.has(cacheKey)) {
      productsGrid.innerHTML = this.#tabCache.get(cacheKey);
      this.#attachProductEventListeners(productsGrid);
      return;
    }

    // Show skeleton loading state
    productsGrid.innerHTML = this.#getSkeletonHTML();

    try {
      // Parse product handles from the products string (comma-separated)
      const productHandles = tab.products.split(',').map(h => h.trim()).filter(h => h);

      if (productHandles.length === 0) {
        const emptyHTML = `
          <div class="cart-modal__empty">
            No products selected
          </div>
        `;
        productsGrid.innerHTML = emptyHTML;
        this.#tabCache.set(cacheKey, emptyHTML);
        return;
      }

      // Fetch all products
      const products = await this.#fetchProducts(productHandles);

      if (products.length === 0) {
        const emptyHTML = `
          <div class="cart-modal__empty">
            No products available
          </div>
        `;
        productsGrid.innerHTML = emptyHTML;
        this.#tabCache.set(cacheKey, emptyHTML);
        return;
      }

      // Build HTML using template literals (faster than string concatenation)
      const productsHTML = this.#buildProductsHTML(products);

      // Cache the HTML
      this.#tabCache.set(cacheKey, productsHTML);

      // Update DOM
      productsGrid.innerHTML = productsHTML;

      // Attach event listeners
      this.#attachProductEventListeners(productsGrid);
    } catch (error) {
      console.error('Failed to load tab products:', error);
      productsGrid.innerHTML = `
        <div class="cart-modal__empty">
          Unable to load products
        </div>
      `;
    }
  }

  /**
   * Prefetches products from adjacent tabs in the background
   * @param {number} currentTabIndex - Current active tab index
   */
  #prefetchAdjacentTabs(currentTabIndex) {
    // Prefetch next tab
    const nextTabIndex = currentTabIndex + 1;
    if (nextTabIndex < this.#tabs.length) {
      this.#prefetchTab(nextTabIndex);
    }

    // Prefetch previous tab
    const prevTabIndex = currentTabIndex - 1;
    if (prevTabIndex >= 0) {
      this.#prefetchTab(prevTabIndex);
    }
  }

  /**
   * Prefetches products for a specific tab without rendering
   * @param {number} tabIndex - Tab index to prefetch
   */
  async #prefetchTab(tabIndex) {
    const tab = this.#tabs[tabIndex];
    if (!tab) return;

    const cacheKey = `tab-${tabIndex}`;
    // Skip if already cached
    if (this.#tabCache.has(cacheKey)) return;

    try {
      // Parse product handles
      const productHandles = tab.products.split(',').map(h => h.trim()).filter(h => h);
      if (productHandles.length === 0) return;

      // Fetch products (this will cache them in #productCache)
      const products = await this.#fetchProducts(productHandles);
      if (products.length === 0) return;

      // Build and cache HTML
      const productsHTML = this.#buildProductsHTML(products);
      this.#tabCache.set(cacheKey, productsHTML);
    } catch (error) {
      // Silently fail for prefetching
      console.debug(`Failed to prefetch tab ${tabIndex}:`, error);
    }
  }

  /**
   * Generates skeleton loading HTML for better perceived performance
   * @returns {string} Skeleton HTML
   */
  #getSkeletonHTML() {
    // Create 6 skeleton cards (typical grid size)
    const skeletonCards = Array.from({ length: 6 }, () => `
      <div class="cart-modal__product-card cart-modal__skeleton">
        <div class="cart-modal__product-card-image-wrapper cart-modal__skeleton-image"></div>
        <div class="cart-modal__product-card-info">
          <div class="cart-modal__skeleton-price"></div>
          <div class="cart-modal__skeleton-title"></div>
          <div class="cart-modal__skeleton-title cart-modal__skeleton-title--short"></div>
          <div class="cart-modal__skeleton-button"></div>
        </div>
      </div>
    `).join('');

    return skeletonCards;
  }

  /**
   * Fetches multiple products by their handles with caching and deduplication
   * @param {Array<string>} handles - Array of product handles
   * @returns {Promise<Array<any>>} Array of products
   */
  async #fetchProducts(handles) {
    const fetchPromises = handles.map(async (handle) => {
      // Check if product is already cached
      if (this.#productCache.has(handle)) {
        return this.#productCache.get(handle);
      }

      // Check if there's already a pending fetch for this product
      if (this.#pendingFetches.has(handle)) {
        return this.#pendingFetches.get(handle);
      }

      // Create new fetch promise
      const fetchPromise = (async () => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (response.ok) {
            const productData = await response.json();
            // Cache the product data
            this.#productCache.set(handle, productData);
            return productData;
          }
        } catch (error) {
          console.error(`Failed to fetch product ${handle}:`, error);
        } finally {
          // Remove from pending fetches
          this.#pendingFetches.delete(handle);
        }
        return null;
      })();

      // Store the pending fetch
      this.#pendingFetches.set(handle, fetchPromise);

      return fetchPromise;
    });

    const results = await Promise.all(fetchPromises);

    // Filter out null results
    return results.filter(p => p !== null);
  }

  /**
   * Builds the products HTML
   * @param {Array} products - Array of products
   * @returns {string} HTML string
   */
  #buildProductsHTML(products) {
    return products
      .map((product) => {
        const firstVariant = product.variants[0];
        const price = firstVariant?.price ? parseFloat(firstVariant.price) : 0;
        const image = product.featured_image || product.images?.[0] || '';
        const badge = product.tags?.find((tag) => ['express', 'new', 'sale'].includes(tag.toLowerCase()));
        const hasMultipleVariants = product.variants && product.variants.length > 1;

        // Get variant information
        const variantId = firstVariant?.id || '';

        return `
        <div class="cart-modal__product-card">
          ${badge ? `<span class="cart-modal__product-badge">${badge}</span>` : ''}
          <div class="cart-modal__product-card-image-wrapper">
            <img
              src="${image}"
              alt="${product.title}"
              class="cart-modal__product-card-image"
              loading="lazy"
            />
          </div>
          <div class="cart-modal__product-card-info">
            <p class="cart-modal__product-card-price">${this.#formatMoney(price)}</p>
            <h4 class="cart-modal__product-card-title">${product.title}</h4>
            <button
              class="cart-modal__product-add-btn"
              data-variant-id="${variantId}"
              data-product-title="${product.title}"
              data-product-handle="${product.handle}"
              data-has-variants="${hasMultipleVariants}"
            >
              ${hasMultipleVariants ? 'Select Options' : 'Add'}
            </button>
          </div>
        </div>
      `;
      })
      .join('');
  }

  /**
   * Attaches event listeners to product add buttons
   * @param {Element} container - Container element
   */
  #attachProductEventListeners(container) {
    // Add buttons
    container.querySelectorAll('.cart-modal__product-add-btn').forEach((btn) => {
      // Remove old listeners by cloning (prevent duplicate listeners)
      const newBtn = btn.cloneNode(true);
      btn.replaceWith(newBtn);

      newBtn.addEventListener('click', (e) => {
        const target = e.currentTarget;
        if (!(target instanceof HTMLElement)) return;

        const variantId = target.dataset.variantId;
        const productTitle = target.dataset.productTitle;
        const productHandle = target.dataset.productHandle;
        const hasVariants = target.dataset.hasVariants === 'true';

        if (hasVariants && productHandle) {
          // Product has multiple variants - show quick add modal
          this.#openQuickAddModal(productHandle);
        } else if (variantId) {
          // Single variant product - add directly to cart
          this.#addToCart(variantId, productTitle || '', target);
        }
      });
    });
  }


  /**
   * Opens the quick-add modal for products with multiple variants
   * @param {string} productHandle - The product handle
   */
  async #openQuickAddModal(productHandle) {
    const quickAddDialog = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!quickAddDialog || !modalContent) {
      console.error('Quick-add dialog not found');
      return;
    }

    try {
      // Open modal immediately for better perceived performance
      // @ts-ignore - quick-add-dialog has showDialog method
      quickAddDialog.showDialog();

      // Show skeleton loading state
      modalContent.innerHTML = `
        <div class="quick-add-modal__skeleton-wrapper">
          <div class="quick-add-modal__skeleton-media">
            <div class="quick-add-modal__skeleton-image"></div>
          </div>
          <div class="quick-add-modal__skeleton-details">
            <div class="quick-add-modal__skeleton-title"></div>
            <div class="quick-add-modal__skeleton-price"></div>
            <div class="quick-add-modal__skeleton-variants">
              <div class="quick-add-modal__skeleton-variant"></div>
              <div class="quick-add-modal__skeleton-variant"></div>
            </div>
            <div class="quick-add-modal__skeleton-button"></div>
          </div>
        </div>
      `;

      // Fetch the product page
      const response = await fetch(`/products/${productHandle}`);
      if (!response.ok) {
        throw new Error('Failed to fetch product page');
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const productGrid = html.querySelector('[data-product-grid-content]');

      if (!productGrid) {
        throw new Error('Product grid not found');
      }

      // Apply mobile transformations if needed
      this.#applyMobileTransformations(productGrid, productHandle);

      // Update the quick-add modal content
      modalContent.innerHTML = productGrid.innerHTML;
    } catch (error) {
      console.error('Error opening quick-add modal:', error);

      // Show error message in modal
      modalContent.innerHTML = `
        <div style="padding: 60px 20px; text-align: center; color: rgb(var(--color-foreground-rgb) / 0.6);">
          <p>Unable to load product details.</p>
          <a href="/products/${productHandle}" style="color: inherit; text-decoration: underline; margin-top: 10px; display: inline-block;">
            View product page
          </a>
        </div>
      `;
    }
  }

  /**
   * Applies mobile-specific transformations to product grid
   * @param {Element} productGrid - The product grid element
   * @param {string} productHandle - The product handle for URL
   */
  #applyMobileTransformations(productGrid, productHandle) {
    // Check if we're on mobile (same logic as quick-add.js)
    const isMobile = window.innerWidth < 750;

    if (!isMobile) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');

    // Get product title from the button data attribute
    const productTitle = this.#productCache.get(productHandle)?.title || '';

    // Create product title link
    const productTitleElement = document.createElement('a');
    productTitleElement.textContent = productTitle;
    productTitleElement.href = `/products/${productHandle}`;

    // Create product header container
    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');

    productHeader.appendChild(productTitleElement);
    if (productPrice) {
      productHeader.appendChild(productPrice);
    }
    productGrid.appendChild(productHeader);

    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }
    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }

    productDetails?.remove();
  }

  /**
   * Adds a product to the cart
   * @param {string} variantId - The variant ID to add
   * @param {string} productTitle - The product title
   * @param {HTMLElement} button - The button that was clicked
   */
  async #addToCart(variantId, productTitle, button) {
    const originalText = button.textContent;

    try {
      // Update button to show loading state
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
      }
      button.textContent = 'Adding...';

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Show success state
      button.textContent = 'Added ✓';
      button.style.background = '#7AB87A';
      button.style.color = 'white';
      button.style.borderColor = '#7AB87A';

      // Refresh cart data (without triggering modal)
      const cart = await this.#fetchCart();

      // Update cart info display
      this.#updateCartInfo(cart.total_price, cart.item_count);

      // Dispatch proper CartUpdateEvent to update cart icon
      document.dispatchEvent(
        new CartUpdateEvent(
          cart,
          variantId,
          {
            itemCount: cart.item_count,
            source: 'cart-modal',
          }
        )
      );

      // Reset button after 1.5 seconds so customer can add again
      setTimeout(() => {
        button.textContent = originalText;
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
        }
        button.style.background = '';
        button.style.color = '';
        button.style.borderColor = '';
      }, 1500);
    } catch (error) {
      console.error('Error adding to cart:', error);

      // Show error state
      button.textContent = 'Error';
      button.style.background = '#fee';
      button.style.color = '#c00';
      button.style.borderColor = '#c00';

      // Reset button after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
        }
        button.style.background = '';
        button.style.color = '';
        button.style.borderColor = '';
      }, 2000);
    }
  }

  /**
   * Formats money according to shop settings
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  #formatMoney(cents) {
    if (typeof Theme !== 'undefined' && Theme.money_format) {
      return this.#formatWithDelimiters(cents, Theme.money_format);
    }
    // Fallback format
    return `KWD ${(cents / 100).toFixed(3)}`;
  }

  /**
   * Formats money with delimiters
   * @param {number} cents - Price in cents
   * @param {string} format - Money format string
   * @returns {string} Formatted price
   */
  #formatWithDelimiters(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(
      /** @type {any} */ number,
      /** @type {any} */ precision,
      /** @type {any} */ thousands,
      /** @type {any} */ decimal
    ) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }
}

if (!customElements.get('cart-modal-component')) {
  customElements.define('cart-modal-component', CartModalComponent);
}
